import { useCallback, useEffect, useLayoutEffect, useRef, useState, type RefObject } from 'react';

export type AnchoredDropdownPosition = {
  top: number;
  left: number;
};

export type AnchoredDropdownOptions = {
  width?: number;
  estimatedHeight?: number;
  margin?: number;
  viewportPadding?: number;
};

export function useAnchoredDropdownPosition<T extends HTMLElement>(
  open: boolean,
  anchorRef: RefObject<T | null>,
  options: AnchoredDropdownOptions = {},
): AnchoredDropdownPosition | null {
  const {
    width = 180,
    estimatedHeight = 220,
    margin = 4,
    viewportPadding = 8,
  } = options;
  const [position, setPosition] = useState<AnchoredDropdownPosition | null>(null);
  const positionRef = useRef<AnchoredDropdownPosition | null>(null);

  useEffect(() => {
    positionRef.current = position;
  }, [position]);

  const clearPosition = useCallback(() => {
    if (positionRef.current === null) return;
    positionRef.current = null;
    setPosition(null);
  }, []);

  const updatePosition = useCallback(() => {
    if (!open || typeof window === 'undefined') {
      clearPosition();
      return;
    }
    const anchor = anchorRef.current;
    if (!anchor || !anchor.isConnected) {
      clearPosition();
      return;
    }

    const rect = anchor.getBoundingClientRect();
    let top = rect.bottom + margin;
    let left = rect.right - width;

    if (left < viewportPadding) left = viewportPadding;
    if (left + width > window.innerWidth - viewportPadding) {
      left = Math.max(viewportPadding, window.innerWidth - viewportPadding - width);
    }
    if (top + estimatedHeight > window.innerHeight - viewportPadding) {
      top = rect.top - estimatedHeight - margin;
    }
    if (top < viewportPadding) top = viewportPadding;

    setPosition((prev) => {
      if (prev && Math.abs(prev.top - top) < 0.5 && Math.abs(prev.left - left) < 0.5) {
        return prev;
      }
      positionRef.current = { top, left };
      return { top, left };
    });
  }, [anchorRef, clearPosition, estimatedHeight, margin, open, viewportPadding, width]);

  useLayoutEffect(() => {
    updatePosition();
  }, [updatePosition]);

  useEffect(() => {
    if (!open || typeof window === 'undefined') return undefined;
    let frame: number | null = null;
    const scheduleUpdate = () => {
      if (frame != null) return;
      frame = window.requestAnimationFrame(() => {
        frame = null;
        updatePosition();
      });
    };

    document.addEventListener('scroll', scheduleUpdate, true);
    window.addEventListener('resize', scheduleUpdate);
    window.addEventListener('orientationchange', scheduleUpdate);

    return () => {
      if (frame != null) window.cancelAnimationFrame(frame);
      document.removeEventListener('scroll', scheduleUpdate, true);
      window.removeEventListener('resize', scheduleUpdate);
      window.removeEventListener('orientationchange', scheduleUpdate);
    };
  }, [open, updatePosition]);

  return position;
}
